import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';

const CampaignReview = ({ formData, uploadedFiles, selectedSlots }) => {
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) {
      return <ImageIcon fontSize="small" sx={{ color: '#2196F3', mr: 1 }} />;
    }
    return <VideoIcon fontSize="small" sx={{ color: '#4CAF50', mr: 1 }} />;
  };

  const getFileType = (file) => {
    if (file.type.startsWith('image/')) {
      return { label: 'Image', color: 'info' };
    }
    return { label: 'Video', color: 'success' };
  };

  return (
    <Box>
      <Alert severity="success" sx={{ mb: 3 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <CheckIcon />
          <Box>
            <Typography variant="h6" gutterBottom>
              Campaign Ready for Submission!
            </Typography>
            <Typography variant="body2">
              Review all details below and submit for admin approval.
            </Typography>
          </Box>
        </Box>
      </Alert>

      {/* Campaign Details Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
          📋 Campaign Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Client Name
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {formData.clientName}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Campaign Name
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {formData.name}
            </Typography>
          </Grid>
          {formData.offerNumber && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Offer Number
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {formData.offerNumber}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Uploaded Media Files Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
          📁 Uploaded Media ({uploadedFiles.length} file{uploadedFiles.length !== 1 ? 's' : ''})
        </Typography>

        {uploadedFiles && uploadedFiles.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>File Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="center">
                    Type
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    Size
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {uploadedFiles.map((file, index) => {
                  const fileType = getFileType(file);
                  return (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          {getFileIcon(file)}
                          <Typography variant="body2">{file.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={fileType.label}
                          size="small"
                          color={fileType.color}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {formatFileSize(file.size)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="warning">No media files uploaded</Alert>
        )}
      </Paper>

      {/* Assigned Slots Section */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 'bold' }}>
          🎬 Assigned Slots ({selectedSlots.length} slot{selectedSlots.length !== 1 ? 's' : ''})
        </Typography>

        {selectedSlots && selectedSlots.length > 0 ? (
          <Grid container spacing={2}>
            {selectedSlots.map((slot, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                      Screen Slot
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Slot {slot.slotNumber}
                    </Typography>
                    <List dense>
                      <ListItem disableGutters>
                        <ListItemText
                          primary="Site Code"
                          secondary={slot.siteCode}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          secondaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                        />
                      </ListItem>
                      {slot.screenName && (
                        <ListItem disableGutters>
                          <ListItemText
                            primary="Screen"
                            secondary={slot.screenName}
                            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                            secondaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      )}
                    </List>
                    <Box display="flex" gap={1} mt={1}>
                      <Chip label="Selected" color="success" size="small" variant="outlined" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Alert severity="warning">No slots assigned</Alert>
        )}
      </Paper>
    </Box>
  );
};

export default CampaignReview;
